// Opportunity Score Calculator for Silk Road Terminal
// Calculates investment opportunity score based on multiple indicators

import type { CountryOverview } from '../../types/country';

export interface OpportunityScoreResult {
  overall: number;           // 0-100
  breakdown: {
    growth: number;          // Economic growth score
    stability: number;       // Macro stability score
    openness: number;        // Trade & investment openness
    demographics: number;    // Demographic dividend
    risk: number;            // Risk-adjusted score (inverted)
  };
  grade: 'A' | 'B' | 'C' | 'D' | 'F';
  label: string;
  signals: OpportunitySignal[];
}

export interface OpportunitySignal {
  type: 'positive' | 'negative' | 'neutral';
  category: string;
  message: string;
  impact: number;  // -10 to +10
}

// Scoring weights
const WEIGHTS = {
  growth: 0.30,
  stability: 0.25,
  openness: 0.20,
  demographics: 0.15,
  risk: 0.10,
};

// Normalize value to 0-100 scale
const normalize = (
  value: number | null,
  min: number,
  max: number,
  invert: boolean = false
): number => {
  if (value === null) return 50; // Default to middle if no data

  const clamped = Math.max(min, Math.min(max, value));
  const normalized = ((clamped - min) / (max - min)) * 100;

  return invert ? 100 - normalized : normalized;
};

// Calculate growth score
const calculateGrowthScore = (data: CountryOverview): { score: number; signals: OpportunitySignal[] } => {
  const signals: OpportunitySignal[] = [];
  let score = 50;

  // GDP Growth (target: 5-7% is excellent)
  if (data.gdp.growth !== null) {
    const growthScore = normalize(data.gdp.growth, -2, 10, false);
    score = growthScore * 0.6;

    if (data.gdp.growth > 6) {
      signals.push({
        type: 'positive',
        category: 'Growth',
        message: `Strong GDP growth at ${data.gdp.growth.toFixed(1)}%`,
        impact: 8,
      });
    } else if (data.gdp.growth < 2) {
      signals.push({
        type: 'negative',
        category: 'Growth',
        message: `Weak GDP growth at ${data.gdp.growth.toFixed(1)}%`,
        impact: -5,
      });
    }
  }

  // FDI Growth
  if (data.fdi.growth !== null) {
    const fdiGrowthScore = normalize(data.fdi.growth, -20, 50, false);
    score += fdiGrowthScore * 0.2;

    if (data.fdi.growth > 15) {
      signals.push({
        type: 'positive',
        category: 'FDI',
        message: `Strong FDI growth at ${data.fdi.growth.toFixed(1)}%`,
        impact: 6,
      });
    }
  }

  // GDP per capita growth potential
  if (data.gdp.perCapita !== null) {
    const perCapitaScore = normalize(data.gdp.perCapita, 500, 15000, false);
    score += perCapitaScore * 0.2;

    if (data.gdp.perCapita < 5000) {
      signals.push({
        type: 'positive',
        category: 'Growth',
        message: 'High catch-up growth potential (lower-middle income)',
        impact: 5,
      });
    }
  }

  return { score: Math.min(100, Math.max(0, score)), signals };
};

// Calculate stability score
const calculateStabilityScore = (data: CountryOverview): { score: number; signals: OpportunitySignal[] } => {
  const signals: OpportunitySignal[] = [];
  let score = 50;

  // Inflation (target: 2-5% is good)
  if (data.inflation.cpi !== null) {
    const inflationScore = normalize(data.inflation.cpi, 0, 20, true);
    score = inflationScore * 0.35;

    if (data.inflation.cpi > 10) {
      signals.push({
        type: 'negative',
        category: 'Stability',
        message: `High inflation at ${data.inflation.cpi.toFixed(1)}%`,
        impact: -6,
      });
    } else if (data.inflation.cpi < 5) {
      signals.push({
        type: 'positive',
        category: 'Stability',
        message: `Well-controlled inflation at ${data.inflation.cpi.toFixed(1)}%`,
        impact: 4,
      });
    }
  }

  // Public Debt (target: <60% is good)
  if (data.debt.publicDebt !== null) {
    const debtScore = normalize(data.debt.publicDebt, 0, 100, true);
    score += debtScore * 0.3;

    if (data.debt.publicDebt < 40) {
      signals.push({
        type: 'positive',
        category: 'Fiscal',
        message: `Low public debt at ${data.debt.publicDebt.toFixed(1)}% of GDP`,
        impact: 5,
      });
    } else if (data.debt.publicDebt > 80) {
      signals.push({
        type: 'negative',
        category: 'Fiscal',
        message: `High public debt at ${data.debt.publicDebt.toFixed(1)}% of GDP`,
        impact: -7,
      });
    }
  }

  // Current Account (target: balanced to surplus)
  if (data.trade.currentAccount !== null) {
    const caScore = normalize(data.trade.currentAccount, -15, 10, false);
    score += caScore * 0.2;

    if (data.trade.currentAccount < -7) {
      signals.push({
        type: 'negative',
        category: 'External',
        message: `Large current account deficit at ${data.trade.currentAccount.toFixed(1)}% of GDP`,
        impact: -4,
      });
    }
  }

  // Reserves coverage
  if (data.reserves.monthsImport !== null) {
    const reservesScore = normalize(data.reserves.monthsImport, 2, 12, false);
    score += reservesScore * 0.15;

    if (data.reserves.monthsImport > 6) {
      signals.push({
        type: 'positive',
        category: 'Reserves',
        message: `Strong reserves cover ${data.reserves.monthsImport.toFixed(1)} months of imports`,
        impact: 4,
      });
    }
  }

  return { score: Math.min(100, Math.max(0, score)), signals };
};

// Calculate openness score
const calculateOpennessScore = (data: CountryOverview): { score: number; signals: OpportunitySignal[] } => {
  const signals: OpportunitySignal[] = [];
  let score = 50;

  // FDI as % of GDP
  if (data.fdi.toGdp !== null) {
    const fdiScore = normalize(data.fdi.toGdp, 0, 15, false);
    score = fdiScore * 0.4;

    if (data.fdi.toGdp > 5) {
      signals.push({
        type: 'positive',
        category: 'Investment',
        message: `High FDI inflows at ${data.fdi.toGdp.toFixed(1)}% of GDP`,
        impact: 6,
      });
    }
  }

  // Trade openness (exports + imports as % of GDP)
  const tradeOpenness = (data.trade.exportsGdp || 0) + (data.trade.importsGdp || 0);
  if (tradeOpenness > 0) {
    const tradeScore = normalize(tradeOpenness, 20, 150, false);
    score += tradeScore * 0.4;

    if (tradeOpenness > 80) {
      signals.push({
        type: 'positive',
        category: 'Trade',
        message: `High trade openness at ${tradeOpenness.toFixed(1)}% of GDP`,
        impact: 4,
      });
    }
  }

  // Export orientation
  if (data.trade.exportsGdp !== null) {
    const exportScore = normalize(data.trade.exportsGdp, 10, 60, false);
    score += exportScore * 0.2;
  }

  return { score: Math.min(100, Math.max(0, score)), signals };
};

// Calculate demographics score
const calculateDemographicsScore = (data: CountryOverview): { score: number; signals: OpportunitySignal[] } => {
  const signals: OpportunitySignal[] = [];
  let score = 50;

  // Population growth
  if (data.population.growth !== null) {
    const popGrowthScore = normalize(data.population.growth, -0.5, 2.5, false);
    score = popGrowthScore * 0.3;

    if (data.population.growth > 1) {
      signals.push({
        type: 'positive',
        category: 'Demographics',
        message: `Growing population at ${data.population.growth.toFixed(1)}% annually`,
        impact: 3,
      });
    }
  }

  // Young population (proxy: if median age is low)
  if (data.population.medianAge !== null) {
    const ageScore = normalize(data.population.medianAge, 20, 50, true);
    score += ageScore * 0.3;

    if (data.population.medianAge < 30) {
      signals.push({
        type: 'positive',
        category: 'Demographics',
        message: 'Young population with demographic dividend potential',
        impact: 5,
      });
    }
  }

  // Market size (population)
  if (data.population.total !== null) {
    const sizeScore = normalize(Math.log10(data.population.total), 6, 9, false);
    score += sizeScore * 0.2;

    if (data.population.total > 30000000) {
      signals.push({
        type: 'positive',
        category: 'Market',
        message: `Large domestic market of ${(data.population.total / 1000000).toFixed(1)}M people`,
        impact: 4,
      });
    }
  }

  // Urbanization
  if (data.population.urbanization !== null) {
    const urbanScore = normalize(data.population.urbanization, 20, 80, false);
    score += urbanScore * 0.2;
  }

  return { score: Math.min(100, Math.max(0, score)), signals };
};

// Calculate risk score (lower is better for opportunity)
const calculateRiskScore = (data: CountryOverview): { score: number; signals: OpportunitySignal[] } => {
  const signals: OpportunitySignal[] = [];
  let riskLevel = 50;

  // External debt risk
  if (data.debt.externalDebt !== null && data.gdp.value !== null) {
    const externalDebtGdp = (data.debt.externalDebt / data.gdp.value) * 100;
    riskLevel += normalize(externalDebtGdp, 0, 100, false) * 0.3;

    if (externalDebtGdp > 80) {
      signals.push({
        type: 'negative',
        category: 'Risk',
        message: 'High external debt vulnerability',
        impact: -5,
      });
    }
  }

  // Credit rating as risk indicator
  if (data.ratings.sp) {
    const ratingScore = getRatingScore(data.ratings.sp);
    riskLevel += (100 - ratingScore) * 0.4;

    if (ratingScore < 50) {
      signals.push({
        type: 'negative',
        category: 'Risk',
        message: `Sub-investment grade rating (${data.ratings.sp})`,
        impact: -4,
      });
    } else if (ratingScore >= 70) {
      signals.push({
        type: 'positive',
        category: 'Risk',
        message: `Investment grade rating (${data.ratings.sp})`,
        impact: 5,
      });
    }
  }

  // High inflation as risk
  if (data.inflation.cpi !== null && data.inflation.cpi > 15) {
    riskLevel += 15;
  }

  // Invert risk to opportunity score (high risk = low opportunity score)
  const score = 100 - Math.min(100, Math.max(0, riskLevel));

  return { score, signals };
};

// Convert credit rating to numeric score
const getRatingScore = (rating: string): number => {
  const ratings: Record<string, number> = {
    'AAA': 100, 'AA+': 95, 'AA': 90, 'AA-': 85,
    'A+': 80, 'A': 75, 'A-': 70,
    'BBB+': 65, 'BBB': 60, 'BBB-': 55,
    'BB+': 50, 'BB': 45, 'BB-': 40,
    'B+': 35, 'B': 30, 'B-': 25, 'B1': 30,
    'CCC+': 20, 'CCC': 15, 'CCC-': 10,
    'CC': 5, 'C': 3, 'D': 0,
  };
  return ratings[rating] || 40;
};

// Get letter grade from score
const getGrade = (score: number): 'A' | 'B' | 'C' | 'D' | 'F' => {
  if (score >= 80) return 'A';
  if (score >= 65) return 'B';
  if (score >= 50) return 'C';
  if (score >= 35) return 'D';
  return 'F';
};

// Get label from score
const getLabel = (score: number): string => {
  if (score >= 80) return 'Excellent Opportunity';
  if (score >= 65) return 'Good Opportunity';
  if (score >= 50) return 'Moderate Opportunity';
  if (score >= 35) return 'Limited Opportunity';
  return 'High Risk';
};

/**
 * Calculate overall opportunity score for a country
 */
export const calculateOpportunityScore = (data: CountryOverview): OpportunityScoreResult => {
  const growthResult = calculateGrowthScore(data);
  const stabilityResult = calculateStabilityScore(data);
  const opennessResult = calculateOpennessScore(data);
  const demographicsResult = calculateDemographicsScore(data);
  const riskResult = calculateRiskScore(data);

  // Calculate weighted overall score
  const overall =
    growthResult.score * WEIGHTS.growth +
    stabilityResult.score * WEIGHTS.stability +
    opennessResult.score * WEIGHTS.openness +
    demographicsResult.score * WEIGHTS.demographics +
    riskResult.score * WEIGHTS.risk;

  // Combine all signals
  const signals = [
    ...growthResult.signals,
    ...stabilityResult.signals,
    ...opennessResult.signals,
    ...demographicsResult.signals,
    ...riskResult.signals,
  ].sort((a, b) => Math.abs(b.impact) - Math.abs(a.impact));

  return {
    overall: Math.round(overall),
    breakdown: {
      growth: Math.round(growthResult.score),
      stability: Math.round(stabilityResult.score),
      openness: Math.round(opennessResult.score),
      demographics: Math.round(demographicsResult.score),
      risk: Math.round(riskResult.score),
    },
    grade: getGrade(overall),
    label: getLabel(overall),
    signals: signals.slice(0, 10), // Top 10 signals
  };
};

export default calculateOpportunityScore;
